/**
 * ESG platform color tokens — single source of truth for MUI theme, CSS, and charts.
 * Recyclee blue brand with slate neutrals tuned for high contrast (WCAG AA+).
 */

export const APP_BRAND = {
  primary: {
    main: '#0000FE',
    light: '#4D4DFF',
    dark: '#0000CC',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#007A66',
    light: '#00A88A',
    dark: '#005C4D',
    contrastText: '#FFFFFF',
  },
} as const;

export const APP_SEMANTIC = {
  success: {
    main: '#1F7A52',
    light: '#2F9E6F',
    dark: '#155C3C',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#C62828',
    light: '#D64545',
    dark: '#9B1C1C',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#A66A12',
    light: '#D4922A',
    dark: '#7A4E0C',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#1E4FB8',
    light: '#3568D4',
    dark: '#163A8A',
    contrastText: '#FFFFFF',
  },
} as const;

/** Accessible text — wired into MUI `palette.text`. */
export const APP_TEXT_COLOR = {
  light: {
    primary: '#0F172A',
    secondary: '#334155',
    disabled: '#64748B',
  },
  dark: {
    primary: '#F8FAFC',
    secondary: '#CBD5E1',
    disabled: '#94A3B8',
  },
} as const;

export const APP_SURFACES = {
  light: {
    default: '#E8EDF3',
    paper: '#FFFFFF',
    elevated: '#F1F5F9',
    input: '#F1F5F9',
    inputFocused: '#FFFFFF',
    shell: '#FFFFFF',
  },
  dark: {
    default: '#0B1017',
    paper: '#151C26',
    elevated: '#1E2733',
    input: '#243041',
    inputFocused: '#2A3748',
    shell: '#151C26',
  },
} as const;

export const APP_BORDERS = {
  light: {
    subtle: 'rgba(15, 23, 42, 0.10)',
    default: 'rgba(15, 23, 42, 0.16)',
    strong: 'rgba(15, 23, 42, 0.28)',
  },
  dark: {
    subtle: 'rgba(255, 255, 255, 0.10)',
    default: 'rgba(255, 255, 255, 0.16)',
    strong: 'rgba(255, 255, 255, 0.28)',
  },
} as const;

export const APP_ACTION = {
  light: {
    hover: 'rgba(15, 23, 42, 0.06)',
    selected: 'rgba(0, 0, 254, 0.14)',
    focusRing: 'rgba(0, 0, 254, 0.22)',
  },
  dark: {
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(77, 77, 255, 0.22)',
    focusRing: 'rgba(77, 77, 255, 0.32)',
  },
} as const;

/** Opaque, mutually distinct hues for multi-segment charts (no alpha wash). */
export const APP_CHART_COLORS = [
  '#0000FE',
  '#007A66',
  '#A66A12',
  '#C62828',
  '#6B21A8',
  '#1E4FB8',
  '#0F766E',
  '#9A3412',
] as const;

export const APP_SHAPE = {
  radiusSm: 10,
  radiusMd: 12,
  radiusLg: 16,
} as const;

export type AppColorMode = 'light' | 'dark';

export const APP_CSS_VARS = {
  primary: APP_BRAND.primary.main,
  primaryRgb: '0, 0, 254',
} as const;

export function getAppSurfaces(mode: AppColorMode) {
  return APP_SURFACES[mode];
}

export function getAppBorders(mode: AppColorMode) {
  return APP_BORDERS[mode];
}

export function getAppAction(mode: AppColorMode) {
  return APP_ACTION[mode];
}
